import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui'
import { useToast } from '@/hooks'
import { useProjectMutations } from '@/modules/projects'
import {
  type DuplicatePageGroup,
  pageRangeLabel,
} from '@/modules/projects/components/duplicates/helpers'
import { useDialogActions, useDialogData, useDialogIsOpen } from '@/stores'

const dialogId = 'delete-duplicate-pages'

// Confirm deleting repeated page ranges. The duplicate copy (and its rows) goes, the original stays.
export const DeleteDuplicatePagesDialog = () => {
  const { successToast, errorToast } = useToast()
  const open = useDialogIsOpen(dialogId)
  const groups = (useDialogData() as DuplicatePageGroup[] | undefined) ?? []
  const { closeDialog } = useDialogActions()
  const { deleteDuplicateSegments } = useProjectMutations()

  const total = groups.length
  const single = total === 1 ? groups[0] : null

  const handleSubmit = () => {
    const segmentIds = groups.map((group) => group.duplicate.id)
    deleteDuplicateSegments(segmentIds)
      .then(() =>
        successToast(
          total === 1
            ? 'Duplicate pages deleted'
            : `${total} duplicate ranges deleted`,
        ),
      )
      .catch((error) => errorToast('Failed to delete duplicate pages', error))
    closeDialog(true)
  }

  return (
    <Dialog open={open} onOpenChange={(next) => !next && closeDialog()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {total === 1
              ? 'Delete these duplicate pages?'
              : 'Delete all duplicates?'}
          </DialogTitle>
          <DialogDescription>
            {single ? (
              <>
                This permanently removes {pageRangeLabel(single.duplicate)} of "
                {single.statement.fileName}" and the transactions on them. The
                original copy stays. This cannot be undone.
              </>
            ) : (
              <>
                This permanently removes {total} repeated page ranges and the
                transactions on them. The original copy of each stays. This
                cannot be undone.
              </>
            )}
          </DialogDescription>
        </DialogHeader>
        <div className="flex justify-end gap-2 pt-4">
          <Button theme="outline" onClick={() => closeDialog()}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} theme="danger">
            {total === 1 ? 'Delete pages' : `Delete ${total} ranges`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

DeleteDuplicatePagesDialog.id = dialogId
